import { Chip } from '@heroui/react';

import { toneOfTag } from '../../services/tagTone';
import { LABELS, TASKS, WEBHOOKS } from '../../i18n/el';

// Τα chips της εφαρμογής, με τις προεπιλογές δεμένες μία φορά εδώ αντί σε κάθε
// σημείο χρήσης.
//
// Τρεις κανόνες που επιβάλλονται από το συστατικό και δεν αφήνονται στην κρίση
// του καλούντος:
//
// 1. **Ανοιχτό φόντο, σκούρο κείμενο.** Το κείμενο του chip είναι 12 πίξελ· σε
//    αυτό το μέγεθος το λευκό πάνω σε σκούρο χάνει λεπτομέρεια στα ελληνικά
//    διακριτικά. Η σχέση είναι αντίστροφη από των κουμπιών, όπου το σκούρο μωβ
//    με λευκό κείμενο δίνει 14.9:1. Στα 12 πίξελ ισχύει ο κανόνας «κύριο χρώμα
//    κειμένου»: κάθε τόνος είναι 7.46:1 ή πάνω, όχι το δεύτερης τάξης χρώμα.
// 2. **Υποχρεωτικό εικονίδιο.** Καμία πληροφορία δεν μεταδίδεται μόνο με χρώμα
//    (WCAG 1.4.1): το «Δημόσιο» ξεχωρίζει από το «Ιδιωτικό» και χωρίς χρώμα.
// 3. **Κεφαλαίο αρχικό.** Τα chips είναι ετικέτες κατάστασης, όχι κώδικας.
//
// Δεν χρησιμοποιούνται τα `color` και `variant` του HeroUI, γιατί ο σύνθετος
// κανόνας του απαιτεί **και τα δύο**: με `variant="soft"` μόνο, το chip βγαίνει
// γκρι αντί για μωβ χωρίς να εμφανιστεί σφάλμα. Οι τόνοι και οι παραλλαγές
// ορίζονται στο `index.css` ως `chip-ds--*`.
//
// Οι αναλογίες αντίθεσης κάθε τόνου ελέγχονται από το `design/build-tokens.mjs`
// και καταγράφονται στο `docs/notes/heroui-migration-plan.md`.

/**
 * Η προεπιλεγμένη παραλλαγή εμφάνισης, σε ένα σημείο.
 *
 * Κρίθηκε 17/08 στο `/design-check`, με τις τριάντα ετικέτες σε πραγματική
 * πυκνότητα ως δείγμα: **μένουν και οι δύο**, γιατί λύνουν διαφορετικό πρόβλημα.
 *
 * - `soft`    — απαλό χρωματιστό φόντο. Για μεμονωμένες καταστάσεις, όπου η
 *               διάκριση πρέπει να είναι ορατή από μακριά.
 * - `outline` — επιφάνεια με χρωματιστό περίγραμμα. Για πυκνές ροές ετικετών,
 *               όπου τριάντα απαλά φόντα γίνονται τριάντα χρωματιστά μπλοκ και
 *               το περιεχόμενο χάνεται.
 *
 * Το `gap` και το `padding` είναι **κοινά** στις δύο παραλλαγές, από τα tokens.
 */
export const DEFAULT_APPEARANCE = 'soft';

/**
 * Βάση για όλα τα chips. Το `icon` είναι υποχρεωτικό: κλάση του Font Awesome 6.
 */
export function StatusChip({
    icon,
    tone = 'neutral',
    appearance = DEFAULT_APPEARANCE,
    children,
    className = '',
    ...rest
}) {
    const classes = ['chip-ds--' + tone, 'chip-ds--' + appearance, className].filter(Boolean).join(' ');

    return (
        <Chip className={classes} {...rest}>
            <i className={icon} aria-hidden="true" />
            {children}
        </Chip>
    );
}

/** ΛΑ-05: το επίπεδο πρόσβασης της συλλογής. */
export function VisibilityChip({ visibility, ...rest }) {
    return visibility === 'public'
        ? <StatusChip icon="fa-solid fa-globe" tone="teal" {...rest}>{LABELS.visibility.public}</StatusChip>
        : <StatusChip icon="fa-solid fa-lock" tone="purple" {...rest}>{LABELS.visibility.private}</StatusChip>;
}

/** ΛΑ-07: η αρχειοθετημένη συλλογή παραμένει αναγνώσιμη αλλά όχι εγγράψιμη. */
export function ArchivedChip(props) {
    return <StatusChip icon="fa-solid fa-box-archive" tone="warning" {...props}>{LABELS.archived}</StatusChip>;
}

/** ΛΑ-04: ο ρόλος του μέλους εντός της συλλογής, κατά τον Πίνακα 1. */
export function RoleChip({ role, ...rest }) {
    return role === 'coordinator'
        ? <StatusChip icon="fa-solid fa-user-gear" tone="purple" {...rest}>{LABELS.role.coordinator}</StatusChip>
        : <StatusChip icon="fa-solid fa-user" tone="neutral" {...rest}>{LABELS.role.member}</StatusChip>;
}

/**
 * Ο **καθολικός** ρόλος του διαχειριστή (Πίνακας 1, `user:manage`).
 *
 * Χωριστό από το `RoleChip`, και η διάκριση δεν είναι αισθητική: εκείνο δείχνει τον
 * ρόλο **εντός μιας συλλογής** — ίδιο πρόσωπο, άλλος ρόλος σε άλλη συλλογή — ενώ
 * αυτό δείχνει ιδιότητα του **λογαριασμού**, που ισχύει παντού. Ένα κοινό chip για
 * τα δύο θα έλεγε ότι είναι το ίδιο είδος πράγματος.
 *
 * Δεν αποδίδεται καθόλου για μη διαχειριστή: η απουσία ρόλου δεν είναι κατάσταση
 * που χρειάζεται σήμα.
 */
export function AdminChip({ isAdmin, ...rest }) {
    if (!isAdmin) return null;
    return (
        <StatusChip icon="fa-solid fa-shield-halved" tone="teal" {...rest}>
            {LABELS.role.admin}
        </StatusChip>
    );
}

/**
 * ΛΑ-11: θεματική ετικέτα πόρου. Το εικονίδιο αντικαθιστά το προθεματικό «#».
 *
 * **Α4: ο τόνος παράγεται από το ΚΕΙΜΕΝΟ της ετικέτας** (`services/tagTone.js`),
 * ώστε η ίδια ετικέτα να έχει πάντοτε το ίδιο χρώμα σε κάθε κάρτα και σε κάθε
 * οθόνη. Ήταν σταθερά μωβ, δηλαδή τριάντα ετικέτες σε ένα χρώμα — το μάτι δεν
 * μπορούσε να ομαδοποιήσει τίποτα.
 *
 * Ο καλών μπορεί να επιβάλει `tone` και υπερισχύει: υπάρχουν σημεία όπου η
 * ετικέτα δεν είναι θέμα αλλά **επιλογή** —το ενεργό φίλτρο, το δείγμα της
 * βιβλιοθήκης— και εκεί το χρώμα σημαίνει κάτι άλλο.
 */
export function TagChip({ children, tone, ...rest }) {
    const resolved = tone ?? toneOfTag(typeof children === 'string' ? children : '');

    return <StatusChip icon="fa-solid fa-hashtag" tone={resolved} {...rest}>{children}</StatusChip>;
}

/** Το θεματικό αντικείμενο της συλλογής (§4.3.2). */
export function TopicChip({ children, ...rest }) {
    return <StatusChip icon="fa-solid fa-tag" tone="neutral" {...rest}>{children}</StatusChip>;
}

/**
 * ΛΑ-19: η κατάσταση ενός καθηκόντος, στις τρεις τιμές που ονομάζει η απαίτηση.
 *
 * Ζει εδώ και όχι μέσα στα συστατικά των καθηκόντων, γιατί είναι **κοινό
 * συστατικό**: τα chips της εφαρμογής ορίζονται σε ένα σημείο, με τον κανόνα του
 * υποχρεωτικού εικονιδίου. Το εικονίδιο δεν είναι διακόσμηση — καμία πληροφορία
 * δεν μεταδίδεται μόνο με χρώμα (WCAG 1.4.1), και οι τρεις καταστάσεις πρέπει να
 * ξεχωρίζουν και σε ασπρόμαυρη εκτύπωση.
 *
 * Άγνωστη τιμή αποδίδεται ως «προς εκτέλεση» **και όχι κενή**: το chip είναι η
 * ένδειξη κατάστασης, και ένα κενό chip θα διαβαζόταν ως «καμία κατάσταση», που
 * δεν υπάρχει στο μοντέλο.
 *
 * ⚠ **ΕΞΑΓΟΜΕΝΟΣ, ΓΙΑΤΙ ΤΟΝ ΔΙΑΒΑΖΕΙ ΚΑΙ Ο ΠΙΝΑΚΑΣ.** Η επικεφαλίδα κάθε στήλης
 * φέρει **το ίδιο** εικονίδιο με την ετικέτα των καρτών της. Δεύτερος κατάλογος
 * στο `TaskBoard` θα ήταν δύο εικονίδια για την ίδια κατάσταση — και θα
 * απέκλιναν σιωπηλά, γιατί κανένα από τα δύο δεν είναι «λάθος» από μόνο του.
 */
export const TASK_STATUS_APPEARANCE = {
    todo: { icon: 'fa-solid fa-circle-notch', tone: 'neutral' },
    in_progress: { icon: 'fa-solid fa-spinner', tone: 'purple' },
    done: { icon: 'fa-solid fa-circle-check', tone: 'teal' }
};

export function TaskStatusChip({ status, ...rest }) {
    const key = TASK_STATUS_APPEARANCE[status] ? status : 'todo';
    const { icon, tone } = TASK_STATUS_APPEARANCE[key];

    return (
        <StatusChip icon={icon} tone={tone} {...rest}>
            {TASKS.status[key]}
        </StatusChip>
    );
}

/**
 * ΛΑ-27: η κατάσταση μιας **παράδοσης** συνδρομής, στις πέντε τιμές του μοντέλου.
 *
 * Ζει εδώ και όχι μέσα στην οθόνη των συνδρομών, για τον ίδιο λόγο με την ετικέτα
 * των καθηκόντων: τα chips της εφαρμογής ορίζονται σε **ένα** σημείο, με τον
 * κανόνα του υποχρεωτικού εικονιδίου. Πέντε καταστάσεις που ξεχωρίζουν μόνο με
 * χρώμα θα παραβίαζαν το 1.4.1 — και οι δύο από αυτές, «απέτυχε» και «δεν
 * στάλθηκε», σημαίνουν εντελώς διαφορετικά πράγματα για τον διαχειριστή: η πρώτη
 * είναι πρόβλημα του τρίτου συστήματος, η δεύτερη **απόφαση του δικού μας**.
 *
 * Άγνωστη τιμή αποδίδεται ως «σε αναμονή» και όχι κενή, με το ίδιο σκεπτικό.
 */
const DELIVERY_APPEARANCE = {
    pending: { icon: 'fa-solid fa-clock', tone: 'neutral' },
    delivering: { icon: 'fa-solid fa-spinner', tone: 'purple' },
    succeeded: { icon: 'fa-solid fa-circle-check', tone: 'teal' },
    failed: { icon: 'fa-solid fa-circle-exclamation', tone: 'danger' },
    suppressed: { icon: 'fa-solid fa-ban', tone: 'neutral' }
};

export function DeliveryStateChip({ state, ...rest }) {
    const key = DELIVERY_APPEARANCE[state] ? state : 'pending';
    const { icon, tone } = DELIVERY_APPEARANCE[key];

    return (
        <StatusChip icon={icon} tone={tone} {...rest}>
            {WEBHOOKS.deliveryStates[key]}
        </StatusChip>
    );
}

export default StatusChip;
